import { Authentication } from "./auth"
import { Language } from "./types"
import { SDKOptions } from "./sdk"
import { State } from "./state"
import { querify } from "./utils/params"

type Info = {
    endpoint?: string
    method?: string
    params?: ParamsType
    status?: number
    code?: string
    msg?: string
    id?: string
    detail?: any
}

export class APIError extends Error {
    message: string
    info: Info

    constructor(message: string, info: Info) {
        super(message) // 'Error' breaks prototype chain here
        this.message = message
        this.info = info
        Object.setPrototypeOf(this, new.target.prototype) // restore prototype chain
    }

    get endpoint() {
        return this.info.endpoint
    }

    get method() {
        return this.info.method?.toUpperCase()
    }

    get params() {
        return this.info.params || {}
    }

    get status() {
        return this.info.status
    }

    get code() {
        return this.info.code
    }

    get type() {
        return this.message
    }

    get msg() {
        return this.info.msg
    }

    get id() {
        return this.info.id
    }

    get detail() {
        return this.info.detail
    }

    toString() {
        return [
            "API call failed:",
            `${this.method} ${this.endpoint} ${JSON.stringify(this.params)} - `,
            this.status,
            this.msg || this.message,
            `(code ${this.code})`,
        ].join(" ")
    }
}

const REQUEST_TIMEOUT = 30000

// ties the caller's signal together with the request timeout. the
// returned release has to run once the response is in, otherwise the timer keeps the tab busy
const abortAfterTimeout = (signal?: AbortSignal) => {
    const controller = new AbortController()

    const timeout = setTimeout(() => {
        const error = new Error("The request timed out.")
        error.name = "TimeoutError"
        controller.abort(error)
    }, REQUEST_TIMEOUT)

    const abort = () => controller.abort(signal?.reason)

    if (signal?.aborted) {
        abort()
    } else {
        signal?.addEventListener("abort", abort)
    }

    return {
        signal: controller.signal,
        release: () => {
            clearTimeout(timeout)
            signal?.removeEventListener("abort", abort)
        },
    }
}

type MethodType = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"
export type AuthType = "customerWeakToken" | "consumerToken" | "customerStrongToken" | "none"
export type ParamsType = { [key: string]: any }
export type Hook = { hookKey: string; hookFn: () => Promise<void> }
export type RequestOptions = {
    language?: Language
    params?: ParamsType
    body?: object | null
    signal?: AbortSignal
    retry?: number
    credentials?: RequestCredentials
}

export class API {
    options: SDKOptions
    state: State
    auth: Authentication
    hooks: Hook[] = []
    #pendingHooks = new Map<string, Promise<void>>()

    constructor(options: SDKOptions, state: State) {
        this.options = options
        this.state = state
        this.auth = new Authentication(options, state, {
            post: this.post.bind(this),
        } as API)
        this.request = this.request.bind(this)
    }

    convertIdsToString(values: any) {
        if (typeof values !== "object" || values === null) return

        Object.keys(values).map(key => {
            if ((key === "id" || key.endsWith("_id")) && typeof values[key] === "number") {
                values[key] = values[key].toString()
                return undefined
            }

            return this.convertIdsToString(values[key])
        })
    }

    addHook(hook: Hook) {
        if (!this.hooks.find(h => h.hookKey === hook.hookKey)) this.hooks.push(hook)
    }

    removeHooks() {
        this.hooks = []
    }

    async callHooks(key: string) {
        const pending = this.#pendingHooks.get(key)
        if (pending) return await pending

        const hooks = this.hooks.filter(hook => hook.hookKey === key)
        if (!hooks.length) return

        const promise = Promise.all(hooks.map(hook => hook.hookFn())).then(() => undefined)
        this.#pendingHooks.set(key, promise)

        try {
            return await promise
        } finally {
            this.#pendingHooks.delete(key)
        }
    }

    // asks the user to log in again through the mounted prompt and reports whether that worked. a
    // rejected prompt means it was cancelled or failed, and the session ends here. both the check
    // before a request and the one after a 401 go through this, so they cannot drift apart
    async reAuthenticate() {
        try {
            await this.callHooks("reAuth")
            return true
        } catch {
            this.auth.logout()
            return false
        }
    }

    async get<T>(endpoint: string, authType: AuthType, options?: RequestOptions): Promise<T> {
        return await this.request("GET", endpoint, authType, options)
    }

    async post<T>(endpoint: string, authType: AuthType, options?: RequestOptions): Promise<T> {
        return await this.request("POST", endpoint, authType, options)
    }

    async patch<T>(endpoint: string, authType: AuthType, options?: RequestOptions): Promise<T> {
        return await this.request("PATCH", endpoint, authType, options)
    }

    async put<T>(endpoint: string, authType: AuthType, options?: RequestOptions): Promise<T> {
        return await this.request("PUT", endpoint, authType, options)
    }

    async delete(endpoint: string, authType: AuthType, options?: RequestOptions) {
        return await this.request("DELETE", endpoint, authType, options)
    }

    async request(
        method: MethodType,
        endpoint: string,
        authType: AuthType,
        options: RequestOptions = {}
    ): Promise<any> {
        if (!this.options.url) {
            throw new Error("SDK has no URL configured to send requests to.")
        }

        const { language = "de", params, body = null, signal, retry = 1, credentials = "omit" } = options

        const baseURL = `${this.options.url}v2.0`

        const query =
            params &&
            Object.keys(params).length &&
            Object.values(params).filter(p => p || typeof p === "boolean").length
                ? "?" + querify(params)
                : ""

        const errorInfo: Info = { endpoint: endpoint, method: method, params: params }

        // a request without the token it needs can only earn a 401, so the token is fetched first.
        // the consumer one comes silently, the strong one costs the user a password prompt - which
        // still beats sending a request that cannot succeed. only on the first attempt: if the
        // retry below still has no token, the 401 path decides, it does not ask a second time
        if (authType === "consumerToken" && !this.state.consumerToken) {
            await this.auth.authConsumer()
        }

        if (
            authType === "customerStrongToken" &&
            !this.state.customerStrongToken &&
            this.state.customerWeakToken &&
            retry > 0
        ) {
            if (!(await this.reAuthenticate())) {
                throw new APIError("AccessDenied", { ...errorInfo, status: 401 })
            }
        }

        // the header is sent even when the token is missing: the api answers a malformed token with
        // a DecodingError, which the 401 handling below can still re-authenticate, while it answers
        // a missing header with AccessDenied, which ends the session right away
        const authHeader = authType !== "none" ? { Authorization: `Bearer ${this.state[authType]}` } : undefined

        const { signal: requestSignal, release } = abortAfterTimeout(signal)

        let response: Response

        // only the fetch itself is guarded. everything below reasons about the response, and a
        // mistake in that reasoning has to surface as the bug it is instead of being reported as
        // a network problem
        try {
            response = await fetch(baseURL + endpoint + query, {
                method: method,
                headers: {
                    accept: "application/json",
                    "content-type": "application/json",
                    "accept-language": language,
                    ...authHeader,
                },
                mode: "cors",
                credentials: credentials,
                body: body ? JSON.stringify(body) : undefined,
                signal: requestSignal,
            })
        } catch (err) {
            release()

            // the caller dropped its interest, for example react-query cancelling a query. that is
            // not a failed request and must not be reported as one
            if (signal?.aborted) {
                throw new APIError("Aborted", errorInfo)
            }

            const name = (err as Error)?.name
            const timedOut = name === "TimeoutError" || name === "AbortError"

            throw new APIError(timedOut ? "Timeout" : "Network error", errorInfo)
        }

        let responseData
        // whether the response carried an actual json payload. a body of `null` announces json
        // but carries nothing, which is how the api answers a successful call with no data
        let hasPayload = false

        try {
            const contentType = response.headers.get("content-type")
            if (response.status !== 204 && contentType?.includes("application/json")) {
                responseData = await response.json()
                hasPayload = responseData !== null && responseData !== undefined
            } else if (response.status !== 204 && response.ok) {
                // the blob is handed over as it is. only the caller knows how long it needs an
                // object url for it, and one created here could never be revoked again
                return await response.blob()
            }
        } catch {
            // the body could not be read or was not the json it announced. an error response still
            // carries its status, a successful one is simply broken
            if (response.ok) {
                throw new APIError("Invalid response", { ...errorInfo, status: response.status })
            }
        } finally {
            release()
        }

        if (!responseData) responseData = {}

        if (response.ok || response.status === 402) {
            this.convertIdsToString(responseData)

            // a body that parsed but carries no data would be handed back as undefined, the one
            // value the return type promises cannot happen. an empty body is fine and stays that
            // way (204, or an endpoint that only signals existence), and so is 402, which is
            // treated as success here although it answers with an error shape
            if (hasPayload && response.ok && !("data" in responseData)) {
                throw new APIError("Invalid response", { ...errorInfo, status: response.status })
            }

            return responseData.data
        }

        const errorData = responseData.error
        this.convertIdsToString(errorData)

        const fail = () =>
            new APIError(errorData?.type || "Unknown error occured", {
                ...errorInfo,
                status: response.status,
                code: errorData?.code,
                msg: errorData?.message,
                id: errorData?.id,
                detail: errorData?.detail,
            })

        if (
            response.status !== 401 ||
            (method === "POST" && endpoint === "/auth/customer" && errorData?.type === "AccessDenied")
        ) {
            throw fail()
        }

        // a 401 either recovers (re-auth, then retry) or ends the session, never both. every
        // giving-up path throws, so a failed call cannot resolve with undefined while its return
        // type promises a response
        const isConsumerAuth = authType === "consumerToken"
        const isCustomerAuth = authType === "customerWeakToken" || authType === "customerStrongToken"
        const canRetry = retry > 0

        // the strong token is short lived by design: not having one is exactly what the re-auth
        // prompt is for, as long as the weak token still identifies the session. a spent retry, a
        // missing weak token or a flat-out AccessDenied leave nothing to re-authenticate with - and
        // neither does a 401 from /auth/customer, the endpoint whose job is checking that very token
        const shouldLogout =
            isCustomerAuth &&
            (!canRetry ||
                !this.state.customerWeakToken ||
                errorData?.type === "AccessDenied" ||
                (method === "GET" && endpoint === "/auth/customer"))

        if (shouldLogout) {
            this.auth.logout()
            throw fail()
        }

        if (isConsumerAuth && canRetry) {
            await this.auth.authConsumer()
            return await this.request(method, endpoint, authType, { ...options, retry: retry - 1 })
        }

        // a rejected prompt already ended the session, so only a successful one retries
        if (isCustomerAuth && (await this.reAuthenticate())) {
            return await this.request(method, endpoint, authType, { ...options, retry: retry - 1 })
        }

        throw fail()
    }
}
